import React, { useMemo, useState } from "react";
import { Button, Col, Divider, Row, Spin, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useAppSelector } from "../../..";
import { ReactComponent as DotsIcon } from "../../../img/3dots.svg";
import {
  checkIsLoading,
  getDictionaryItemById,
  geDicValueById,
  toDisplayFormat,
} from "../../../lib/utils";
import { DestinationType } from "../../../store/slices/destinations/types";
import { ReactComponent as VersionsHiddenIcon } from "../../../img/versions_icons/versionsHidden_icon.svg";
import { ReactComponent as VersionsShownIcon } from "../../../img/versions_icons/versionsShown_icon.svg";
import { hasAccess } from "../../RolesModule/hasAccess";
import HistoryAndPropertyModal from "../../HistoryAndPropertyModal/HistoryAndPropertyModal";
import { MENU_TC_ADMIN_DESTINATIONS_WRITE } from "../../../lib/const/permissions";
import "./DestinationView.less";

interface TableDataRecord {
  key: string;
  areaCodes: DestinationType["areaCodes"];
  period: string;
  isFutureVersion: boolean;
  isCurrentVersion: boolean;
  isPastVersion: boolean;
}

interface ComponentProps {
  onOpenVersions: () => void;
  onCloseVersions: () => void;
}

/* ---------------------------------------------- Начало компонента ---------------------------------------------- */

export const DestinationView = (props: ComponentProps) => {
  const { onOpenVersions, onCloseVersions } = props;
  const [isVersionsOpen, setIsVersionsOpen] = useState<boolean>(false);

  const {
    countries,
    countriesLoading,
    regions,
    regionsLoading,
    destinationCategories,
    destinationCategoriesLoading,
    destinationTypes,
    destinationTypesLoading,
  } = useAppSelector((state) => state.dicts);

  const {
    destination,
    destinationLoading,
    parentDestination,
    parentDestinationLoading,
    destinationSets,
    destinationSetsLoading,
    destinationVersions,
    destinationVersionsLoading,
  } = useAppSelector((state) => state.destinations);

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  const showHistoryModal = (recId) => {
    setHistoryModalVisible(true);
    setObjectId(recId);
  };
  const destinationSetName: string = useMemo(() => {
    if (!destination) return "";
    return geDicValueById(destinationSets, destination.destinationSetId)?.name || "";
  }, [destination, destinationSets]);

  const countryName: string = useMemo(() => {
    if (!destination) return "";
    return getDictionaryItemById(countries, destination.countryId)?.name || "";
  }, [destination, countries]);

  const regionName: string = useMemo(() => {
    if (!destination) return "";
    const regionId = getDictionaryItemById(countries, destination.countryId)?.parentDicId;
    return getDictionaryItemById(regions, regionId)?.name || "";
  }, [destination, countries, regions]);

  const fullName: string = useMemo(
    () => [destination?.path, destination?.name].filter(Boolean).join(" - "),
    [destination]
  );

  const categoryName: string = useMemo(() => {
    if (!destination) return "";
    return getDictionaryItemById(destinationCategories, destination.categoryId)?.name || "";
  }, [destination, destinationCategories]);

  const fullShortName: string = useMemo(
    () => [destination?.shortPath, destination?.shortName].filter(Boolean).join(" - "),
    [destination]
  );

  const destinationTypeName: string = useMemo(() => {
    if (!destination) return "";
    return getDictionaryItemById(destinationTypes, destination.destinationTypeId)?.name || "";
  }, [destination, destinationTypes]);

  const spinning: boolean = useMemo(
    () =>
      checkIsLoading(
        destinationLoading,
        destinationSetsLoading,
        countriesLoading,
        regionsLoading,
        parentDestinationLoading,
        destinationCategoriesLoading,
        destinationTypesLoading
      ),
    [
      destinationLoading,
      destinationSetsLoading,
      countriesLoading,
      regionsLoading,
      parentDestinationLoading,
      destinationCategoriesLoading,
      destinationTypesLoading,
    ]
  );

  const columns: ColumnsType<TableDataRecord> = [
    {
      title: "Коды",
      key: "areaCodes",
      className: "destination-view-areaCodes",
      render: (v, { areaCodes }) => (
        <>
          {areaCodes.map((code, i) => (
            <div key={i}>{code}</div>
          ))}
        </>
      ),
      width: "80%",
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: "15%",
    },
  ];

  const columnsChild: ColumnsType<TableDataRecord> = [
    {
      title: "Коды",
      key: "areaCodes",
      className: "destination-view-areaCodes",
      render: (v, { areaCodes }) => (
        <>
          {areaCodes.map((code, i) => (
            <div key={i}>{code}</div>
          ))}
        </>
      ),
      width: "80%",
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: "15%",
    },
    {
      title: "",
      key: "eye",
      dataIndex: "eye",
    },
  ];

  const childTableData = useMemo(
    (): TableDataRecord[] =>
      destinationVersions.map((version, i) => {
        const fromDate = version.fromDate && toDisplayFormat(version.fromDate);
        const toDate = version.toDate && toDisplayFormat(version.toDate);
        const isFutureVersion = version?.fromDate
          ? moment(version.fromDate).isAfter(moment())
          : false;
        const isCurrentVersion =
          version?.fromDate && version?.toDate
            ? moment(version.fromDate).isBetween(version.fromDate, version.toDate)
            : false;
        const isPastVersion = version?.toDate ? moment(version.toDate).isBefore(moment()) : false;
        const period: string = [fromDate, toDate].filter(Boolean).join(" - ");
        return {
          key: `${version.id}_${i}`,
          areaCodes: version?.areaCodes,
          period,
          isFutureVersion,
          isCurrentVersion,
          isPastVersion,
        };
      }),
    [destinationVersions]
  );

  const innerRowClassName = (record: TableDataRecord) => {
    if (record.isCurrentVersion) {
      return `row_child row_current-version`;
    }
    if (record.isPastVersion) {
      return "row_child row_past-version";
    }
    return "row_child";
  };

  const expandedRowRender = () => {
    return (
      <Table
        columns={columnsChild}
        className="table_no-hover"
        dataSource={childTableData}
        rowClassName={innerRowClassName}
        loading={checkIsLoading(destinationVersionsLoading)}
        pagination={false}
        showHeader={false}
        size="small"
      />
    );
  };

  const dataSource = useMemo((): TableDataRecord[] => {
    const fromDate = destination?.fromDate && toDisplayFormat(destination.fromDate);
    const toDate = destination?.toDate && toDisplayFormat(destination.toDate);
    const isFutureVersion = destination?.fromDate
      ? moment(destination.fromDate).isAfter(moment())
      : false;
    const isCurrentVersion =
      destination?.fromDate && destination?.toDate
        ? moment(destination.fromDate).isBetween(destination.fromDate, destination.toDate)
        : false;
    const isPastVersion = destination?.toDate
      ? moment(destination.toDate).isBefore(moment())
      : false;
    const period: string = [fromDate, toDate].filter(Boolean).join(" - ");
    return [
      {
        key: "1",
        areaCodes: destination?.areaCodes || [],
        period,
        isFutureVersion,
        isCurrentVersion,
        isPastVersion,
      },
    ];
  }, [destination]);

  const onClickVersionIcon = () => {
    if (isVersionsOpen) {
      onCloseVersions();
      setIsVersionsOpen(false);
    } else {
      onOpenVersions();
      setIsVersionsOpen(true);
    }
  };

  return (
    <Spin spinning={spinning}>
      <div className="destination-view">
        <Row gutter={30}>
          <Col span={3}>
            <label>Набор</label>
          </Col>
          <Col span={8}>{destinationSetName}</Col>
          <Col span={3}>
            <label>Страна</label>
          </Col>
          <Col span={7}>{countryName}</Col>
        </Row>
        <Row gutter={30} className="destination-view-row">
          <Col span={3}>
            <label>Наименование</label>
          </Col>
          <Col span={8}>{destination?.name}</Col>
          <Col span={3}>
            <label>Регион</label>
          </Col>
          <Col span={7}>{regionName}</Col>
        </Row>
        <Row gutter={30} className="destination-view-row">
          <Col span={3}>
            <label>Аббревиатура</label>
          </Col>
          <Col span={8}>{destination?.shortName}</Col>
          <Col span={3}>
            <label>Родительское направление</label>
          </Col>
          <Col span={7}>
            {[parentDestination?.name, parentDestination?.shortName].filter(Boolean).join(" - ")}
          </Col>
        </Row>
        <Row gutter={30} className="destination-view-row">
          <Col span={3}>
            <label>Полное наименование</label>
          </Col>
          <Col span={8}>{fullName}</Col>
          <Col span={3}>
            <label>Категория</label>
          </Col>
          <Col span={7}>{categoryName}</Col>
        </Row>
        <Row gutter={30} className="destination-view-row">
          <Col span={3}>
            <label>Полная аббревиатура</label>
          </Col>
          <Col span={8}>{fullShortName}</Col>
          <Col span={3}>
            <label>Тип</label>
          </Col>
          <Col span={7}>{destinationTypeName}</Col>
        </Row>
        <Divider style={{ margin: "30.5px 0 8px" }} />
        <Row justify="space-between">
          <h3>Коды в направлении</h3>
          <Col style={{ top: "-2px" }}>
            <Row align="middle" wrap={false}>
              <Col span={12}>
                <Button
                  icon={isVersionsOpen ? <VersionsShownIcon /> : <VersionsHiddenIcon />}
                  type="text"
                  className="button-icon destination-view-version-icon"
                  htmlType="button"
                  onClick={onClickVersionIcon}
                />
              </Col>
              <Col span={12}>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    showHistoryModal(destination?.id);
                  }}
                  className="iconButton"
                  type="text"
                  htmlType="button"
                  icon={<DotsIcon />}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Divider style={{ margin: "0 0 9px" }} />
        <Table
          size="small"
          columns={columns}
          dataSource={dataSource}
          expandable={{
            expandedRowRender,
            expandedRowKeys: isVersionsOpen ? ["1"] : [],
            columnWidth: 0,
            expandIconColumnIndex: 2,
            expandIcon: () => null,
          }}
          pagination={false}
        />
        <HistoryAndPropertyModal
          objectTypeSysname="destination"
          objectId={objectId}
          handleHideModal={() => setHistoryModalVisible(false)}
          visibleModal={historyModalVisible}
          hasAccessToEdit={hasAccess(MENU_TC_ADMIN_DESTINATIONS_WRITE)}
        />
      </div>
    </Spin>
  );
};
